import type { BusinessStatus, OnboardingStatus } from '@Store/slices/customer/customer.types.ts';

export interface AdditionalDocumentRequest {
  llcFileType?: keyof typeof BUSINESS_FILE_TYPES;
  docRequestReasonEnum: string;
  header: string;
  message: string;
  instructions: string;
}

export interface KybData {
  success?: boolean;
  onboardingStatus?: OnboardingStatus;
  business: {
    externalId: string;
    name: string;
    naicsIndustry: string;
    businessType: string;
    status: BusinessStatus;
    missingLlcDocuments: (keyof typeof BUSINESS_FILE_TYPES)[];
    missingFileForNonUsUbos?: MissingFileForNonUsUbo[];
    primaryOwnershipPercent: number;
    requestedDocsData?: AdditionalDocumentRequest[];
  };
}
export interface MissingFileForNonUsUbo {
  externalId: string;
  name: string;
  fileType: keyof typeof BUSINESS_FILE_TYPES;
}
export enum BUSINESS_FILE_TYPES {
  IRS_EIN_LETTER = 'IRS_EIN_LETTER',
  ARTICLES_OF_ORGANIZATION = 'ARTICLES_OF_ORGANIZATION',
  ARTICLES_OF_INCORPORATION = 'ARTICLES_OF_INCORPORATION',
  LETTER_OF_REINSTATEMENT = 'LETTER_OF_REINSTATEMENT',
  GOVERNMENT_IDENTIFICATION = 'GOVERNMENT_IDENTIFICATION',
  SIGNED_PARTNERSHIP_AGREEMENT = 'SIGNED_PARTNERSHIP_AGREEMENT',
  UTILITY_BILL = 'UTILITY_BILL',
  OPERATING_AGREEMENT = 'OPERATING_AGREEMENT',
  PASSPORT = 'PASSPORT',
  NFP_DETERMINATION_LETTER = 'NFP_DETERMINATION_LETTER',
  AMENDMENT_DOCUMENT = 'AMENDMENT_DOCUMENT',
  PROOF_OF_OWNERSHIP = 'PROOF_OF_OWNERSHIP',
  MARKETING_DOCUMENT = 'MARKETING_DOCUMENT',
  REQUEST_FOR_INFORMATION = 'REQUEST_FOR_INFORMATION',
}

export interface IKybSlice {
  kybData: KybData;
  setKybData: (data: KybData) => void;
  getKybData: () => KybData;
  prevOnboardingStatus: OnboardingStatus | null;
  setPrevOnboardingStatus: (status: OnboardingStatus | null) => void;
}

export const businessFileTypesImagesMap = {
  IRS_EIN_LETTER: [
    'https://assets.lili.co/onboarding/verification_center/IRS_EIN_LETTER_1.svg',
    'https://assets.lili.co/onboarding/verification_center/IRS_EIN_LETTER_2.svg',
    'https://assets.lili.co/onboarding/verification_center/IRS_EIN_LETTER_3.png',
  ],
  ARTICLES_OF_ORGANIZATION: [
    'https://assets.lili.co/onboarding/verification_center/ARTICLES_OF_ORGANIZATION_MULTI_NEW.svg',
    'https://assets.lili.co/onboarding/verification_center/ARTICLES_OF_ORGANIZATION_SINGLE.svg',
  ],
  ARTICLES_OF_INCORPORATION: [
    'https://assets.lili.co/onboarding/verification_center/ARTICLES_OF_INCORPORATION_MULTI.svg',
    'https://assets.lili.co/onboarding/verification_center/ARTICLES_OF_INCORPORATION_SINGLE.svg',
  ],
  LETTER_OF_REINSTATEMENT: [
    'https://assets.lili.co/onboarding/verification_center/LETTER_OF_REINSTATEMENT.svg',
  ],
  GOVERNMENT_IDENTIFICATION: [
    'https://assets.lili.co/onboarding/verification_center/GOVERNMENT_IDENTIFICATION.png',
  ],
  SIGNED_PARTNERSHIP_AGREEMENT: [
    'https://assets.lili.co/onboarding/verification_center/SIGNED_PARTNERSHIP_AGREEMENT.svg',
  ],
  UTILITY_BILL: ['https://assets.lili.co/onboarding/verification_center/UTILITY_BILL_v3.svg'],
  OPERATING_AGREEMENT: [
    'https://assets.lili.co/onboarding/verification_center/OPERATING_AGREEMENT.png',
  ],
  FINANCIAL_REPORT: ['https://assets.lili.co/onboarding/verification_center/FINANCIAL_REPORT.svg'],
  BANK_STATEMENT: ['https://assets.lili.co/onboarding/verification_center/BANK_STATEMENT.png'],
  PASSPORT: ['https://assets.lili.co/onboarding/verification_center/passport.png'],
  NFP_DETERMINATION_LETTER: [
    'https://assets.lili.co/onboarding/verification_center/NFP_DETERMINATION_LETTER.png',
  ],
  AMENDMENT_DOCUMENT: [
    'https://assets.lili.co/onboarding/verification_center/AMENDMENT_DOCUMENT.svg',
  ],
  PROOF_OF_OWNERSHIP: [
    'https://assets.lili.co/onboarding/verification_center/PROOF_OF_OWNERSHIP.svg',
  ],
  MARKETING_DOCUMENT: [
    'https://assets.lili.co/onboarding/verification_center/MARKETING_DOCUMENT.svg',
  ],
  REQUEST_FOR_INFORMATION: [],
};

export const getBusinessFileTypesImages = (
  fileType: keyof typeof BUSINESS_FILE_TYPES,
  uboCount: number
): string[] => {
  const multipleUbosFileTypes = [
    BUSINESS_FILE_TYPES.ARTICLES_OF_ORGANIZATION,
    BUSINESS_FILE_TYPES.ARTICLES_OF_INCORPORATION,
  ];
  const images = businessFileTypesImagesMap[fileType];

  if (fileType === BUSINESS_FILE_TYPES.IRS_EIN_LETTER) {
    return images;
  }

  if (
    images.length > 1 &&
    uboCount > 0 &&
    multipleUbosFileTypes.includes(fileType as BUSINESS_FILE_TYPES)
  ) {
    return [images[0]];
  } else if (
    images.length > 1 &&
    uboCount === 0 &&
    multipleUbosFileTypes.includes(fileType as BUSINESS_FILE_TYPES)
  ) {
    return [images[1]];
  } else if (images.length) {
    return [images[0]];
  } else {
    return [];
  }
};

export const businessFileTypesImages = businessFileTypesImagesMap;
